import type { ParentCategory } from '@/types';
import type { SpaceDetail, SpaceSendRuleType } from '@/types/spaces';
import type { SpaceExpenseOverviewRow } from '@/types/space-settlements';
import { hasCommitments } from './commitments';
import { RULE_TYPE_LABELS } from './rules';
import { RUNNING_LEDGER, participantName } from './settlement';

export type SpaceExpenseRow = SpaceExpenseOverviewRow;
export const EXPENSES_PAGE_SIZE = 20;

// `upcoming` is not an expense filter: it swaps the list for the space's recurring commitments, which only a
// monthly space has.
export function expenseFilter(space: SpaceDetail, value: string | null): string {
  if (value === 'auto') return 'auto';
  if (value === 'upcoming' && hasCommitments(space.kind)) return 'upcoming';
  const payer = value?.startsWith('payer:') ? value.slice(6) : null;
  return payer && space.members.some((member) => member.userId === payer) ? `payer:${payer}` : 'all';
}

export function isAutomatic(expense: Pick<SpaceExpenseRow, 'via'>): boolean {
  return expense.via === 'rule' || expense.via === 'recurring';
}

// The overview keeps the payer's name at send time, so someone who left still reads by name rather than by id.
export function expensePayerName(space: SpaceDetail, expense: Pick<SpaceExpenseRow, 'payerUserId' | 'payerName'>): string {
  const current = space.members.some((member) => member.userId === expense.payerUserId);
  return (!current && expense.payerName?.trim()) || participantName(space, expense.payerUserId);
}

// A frozen split can include someone who left; the overview only names them when they are this expense's payer.
export function expenseMemberName(space: SpaceDetail, expense: Pick<SpaceExpenseRow, 'payerUserId' | 'payerName'>, userId: string): string {
  return userId === expense.payerUserId ? expensePayerName(space, expense) : participantName(space, userId);
}

// `source.rule` only arrives for the viewer's own rule; its value is payer-only, so it stays apart for `data-private`.
export function expenseEntry(space: SpaceDetail, expense: SpaceExpenseRow, userId: string): { label: string; privateValue: string | null } {
  const payer = expensePayerName(space, expense);
  const mine = expense.payerUserId === userId;
  if (!isAutomatic(expense)) return { label: mine ? 'enviada por você' : `enviada por ${payer}`, privateValue: null };
  const rule = expense.source?.rule;
  // Only a description rule's value is display text; category, tag, merchant and recurring values are internal ids.
  const value = rule?.type === 'description' ? rule.value : null;
  // Someone else's rule arrives only in the payer's own client, previewing the space as another member: the value is
  // still theirs, so it stays apart (and blurred) next to the words the other member reads.
  if (!mine) return { label: `automática · regra de ${payer}`, privateValue: value };
  if (value !== null) return { label: 'sua regra', privateValue: value };
  const kind = rule ? RULE_TYPE_LABELS[rule.type as SpaceSendRuleType] : undefined;
  return { label: kind ? `sua regra (${kind.toLocaleLowerCase('pt-BR')})` : 'sua regra', privateValue: null };
}

export function expensePermissions(space: SpaceDetail, expense: SpaceExpenseRow, userId: string): { remove: boolean; except: boolean } {
  const open = space.myRole === 'editor' && !space.archivedAt;
  const mine = expense.payerUserId === userId;
  // Exclusions live in the sender's own rule set, so only the sender may create one.
  return { remove: open, except: open && mine && expense.via === 'rule' };
}

// "Ajustar" (ruled 2026-09-20): the send flow lives in the personal view, so the payer is taken to the source
// transaction there instead of the send drawer opening inside a space. There is no per-transaction route, so the
// link narrows the list to the days around the charge (a day each side absorbs the local/UTC date difference).
export function expenseAdjustHref(expense: Pick<SpaceExpenseRow, 'payerUserId' | 'source' | 'occurredAt'>, userId: string): string | null {
  if (expense.payerUserId !== userId || !expense.source?.transactionId) return null;
  const day = (offset: number) => new Date(Date.parse(expense.occurredAt.slice(0, 10)) + offset * 86_400_000).toISOString().slice(0, 10);
  return `/transactions?${new URLSearchParams({ period: 'custom', from: day(-1), to: day(1) })}`;
}

export function categoryLookup(categories: ParentCategory[] | undefined): Map<string, { label: string; icon: string | null }> {
  const lookup = new Map<string, { label: string; icon: string | null }>();
  for (const parent of categories ?? []) {
    lookup.set(parent.id, { label: parent.descriptionTranslated, icon: parent.icon });
    for (const child of parent.children) lookup.set(child.id, { label: child.descriptionTranslated, icon: child.icon ?? parent.icon });
  }
  return lookup;
}

export function expenseDate(occurredAt: string, style: 'short' | 'long' = 'short'): string {
  const options: Intl.DateTimeFormatOptions = style === 'short' ? { day: '2-digit', month: 'short' } : { day: 'numeric', month: 'long', year: 'numeric' };
  return new Date(occurredAt).toLocaleDateString('pt-BR', { ...options, timeZone: 'UTC' });
}

// Mirrors the ledger's UTC month window so legacy assigned spaces, which have no ledger, read the same period.
// A month key, or a span of whole months (`yyyy-MM..yyyy-MM`, see `spacePeriodFromSelection`): the first month's
// first day and the last month's last day, which the overview reads through its end (VER-571).
export function expenseBounds(period: string): Record<string, string> {
  // A trip and the running balance have no bounds: every expense of the space.
  if (period === 'trip' || period === RUNNING_LEDGER) return {};
  const [first, last = first] = period.split('..');
  const end = new Date(`${last}-01T00:00:00Z`);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  return { from: `${first}-01`, to: end.toISOString().slice(0, 10) };
}

const MEMBER_COLORS = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5', 'bg-chart-6'];

// Roster order keeps one colour per person across rows; departed members of a frozen split share the next slot.
export function memberColor(space: SpaceDetail, userId: string): string {
  const index = space.members.findIndex((member) => member.userId === userId);
  return MEMBER_COLORS[(index === -1 ? space.members.length : index) % MEMBER_COLORS.length];
}
